import {
  Box,
  Card,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  type SxProps,
  type Theme,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import type { ReactNode } from 'react';
import { displaySx, numericSx } from '../../../theme/typography';

export type StatTileTone =
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info'
  | 'accent'
  | 'neutral';

export interface StatTileTrend {
  /** Numeric delta. Sign drives the icon + colour when `label` is omitted. */
  value: number;
  /** Optional pre-formatted label (e.g. "+£820k", "↓ 18%"). */
  label?: string;
  /** Optional explicit tone override; auto-derived from `value` otherwise. */
  tone?: StatTileTone;
}

interface StatTileProps {
  /** 11/600 uppercase label, ink-3. */
  label: string;
  /** 30px display value (tabular-nums). */
  value: ReactNode;
  /** Optional tooltip on the label (hover for definition). */
  labelTooltip?: string;
  /** Optional secondary line beneath the value (11.5/ink-3). */
  sub?: ReactNode;
  /** Optional trend pill in the top-right of the header row. */
  trend?: StatTileTrend;
  /** Optional gear / configure handler (renders a small icon button). */
  onConfigure?: () => void;
  /** Accessible label for the configure button. */
  configureAriaLabel?: string;
  /**
   * Accent for the value text — defaults to `text.primary`. Use sparingly;
   * the prototype reserves coloured values for genuinely important
   * indicators (e.g. utilisation, status).
   */
  valueColor?: string;
  /** Optional extra slot rendered beneath the value (e.g. a soft bar). */
  children?: ReactNode;
  loading?: boolean;
  'data-testid'?: string;
}

/**
 * Canonical KPI tile for the redesigned Dashboard. Mirrors the prototype's
 * `StatTile` (the v2 prototype):
 * label → optional trend pill → value → optional sub → optional children.
 * Plain neutral surface (no gradient wash, no coloured border, no icon
 * chip) — colour identity lives in the value text and the trend pill.
 *
 * Always pair with `numericSx` on the value (already applied internally).
 */
export function StatTile({
  label,
  value,
  labelTooltip,
  sub,
  trend,
  onConfigure,
  configureAriaLabel,
  valueColor = 'text.primary',
  children,
  loading,
  'data-testid': testId,
}: StatTileProps) {
  const labelNode = (
    <Typography
      component="div"
      sx={{
        fontSize: '0.6875rem', // 11px
        fontWeight: 600,
        color: 'text.disabled',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        lineHeight: 1.4,
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        p: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: 130,
        height: '100%',
      }}
      data-testid={testId}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        {labelTooltip ? (
          <Tooltip title={labelTooltip} arrow>
            <Box>{labelNode}</Box>
          </Tooltip>
        ) : (
          labelNode
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {trend ? <StatTileTrendPill trend={trend} /> : null}
          {onConfigure ? (
            <IconButton
              size="small"
              onClick={onConfigure}
              aria-label={configureAriaLabel ?? 'Configure tile'}
              sx={{
                p: 0.25,
                color: 'text.disabled',
                '&:hover': { color: 'text.primary' },
              }}
            >
              <SettingsIcon sx={{ fontSize: 14 }} />
            </IconButton>
          ) : null}
        </Box>
      </Box>
      <Typography
        component="div"
        sx={[
          displaySx,
          numericSx,
          {
            fontSize: '1.875rem', // 30px
            fontWeight: 500,
            color: valueColor,
            lineHeight: 1.05,
            mt: '6px',
          },
        ]}
      >
        {loading ? '…' : value}
      </Typography>
      {sub ? (
        <Typography
          component="div"
          sx={{
            fontSize: '0.71875rem', // 11.5px
            color: 'text.disabled',
            lineHeight: 1.4,
          }}
        >
          {sub}
        </Typography>
      ) : null}
      {children}
    </Card>
  );
}

function toneToColours(tone: StatTileTone, theme: Theme) {
  switch (tone) {
    case 'ok':
      return { bg: theme.palette.statusSoft.ok, fg: theme.palette.success.main };
    case 'warn':
      return { bg: theme.palette.statusSoft.warn, fg: theme.palette.warning.main };
    case 'danger':
      return { bg: theme.palette.statusSoft.danger, fg: theme.palette.error.main };
    case 'info':
      return { bg: theme.palette.statusSoft.info, fg: theme.palette.info.main };
    case 'accent':
      return { bg: theme.palette.accent.tint, fg: theme.palette.primary.main };
    case 'neutral':
    default:
      return {
        bg: alpha(theme.palette.text.disabled, 0.15),
        fg: theme.palette.text.secondary,
      };
  }
}

function autoTone(value: number, explicit?: StatTileTone): StatTileTone {
  if (explicit) return explicit;
  if (value > 0) return 'ok';
  if (value < 0) return 'danger';
  return 'neutral';
}

function StatTileTrendPill({ trend }: { trend: StatTileTrend }) {
  const tone = autoTone(trend.value, trend.tone);
  const positive = trend.value > 0;
  const Icon = positive ? TrendingUpIcon : TrendingDownIcon;
  const label =
    trend.label ??
    `${positive ? '+' : ''}${
      Number.isInteger(trend.value)
        ? trend.value
        : trend.value.toFixed(2)
    }%`;
  const pillSx: SxProps<Theme> = (theme) => {
    const { bg, fg } = toneToColours(tone, theme);
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.25,
      backgroundColor: bg,
      color: fg,
      fontSize: '0.6875rem',
      fontWeight: 600,
      px: '7px',
      py: '2px',
      borderRadius: 999,
      lineHeight: 1.4,
    };
  };
  return (
    <Box sx={pillSx}>
      {trend.value !== 0 ? <Icon sx={{ fontSize: 12 }} /> : null}
      <Box component="span" sx={numericSx}>
        {label}
      </Box>
    </Box>
  );
}
